/**
 * Job Tracker Utility
 * 
 * Handles polling for background job status with automatic retry and cleanup.
 * Frontend polls /api/job_status/{jobId} every 3 seconds.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Invalid job IDs that should not trigger polling
const INVALID_JOB_IDS = ['starting', 'uploading', 'pending', 'null', 'undefined', ''];

class JobTracker {
  constructor(jobId, callbacks = {}) {
    this.jobId = jobId;
    this.onProgress = callbacks.onProgress || (() => {});
    this.onComplete = callbacks.onComplete || (() => {});
    this.onError = callbacks.onError || (() => {});
    
    this.pollInterval = null;
    this.pollDelay = 3000; // 3 seconds
    this.isRunning = false;
    this.errorCount = 0;
    this.maxErrors = 5; // Stop after 5 consecutive errors
  }

  /**
   * Check if job ID is valid for polling
   */
  isValidJobId() {
    if (!this.jobId) return false;
    if (INVALID_JOB_IDS.includes(this.jobId.toLowerCase())) return false;
    // Valid UUIDs are typically 36 characters (with dashes)
    if (this.jobId.length < 10) return false;
    return true;
  }

  /**
   * Start polling for job status
   */
  start() {
    // POLLING DISABLED - user preference
    console.log(`⚠️ Polling disabled for job ${this.jobId}`);
    return;
  }

  /**
   * Poll job status from API
   * Uses the unified /api/job-status endpoint which handles both APF and RETAIL workflows
   */
  async poll() {
    if (!this.isRunning) return;

    try {
      const token = localStorage.getItem('token');
      let job = null;
      
      // Use the new unified job status endpoint (handles both APF and RETAIL)
      const response = await fetch(`${API_BASE_URL}/api/job-status/${this.jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        job = await response.json();
      } else if (response.status === 404) {
        // Fallback: try legacy endpoints for older jobs
        try {
          const retailResponse = await fetch(`${API_BASE_URL}/api/retail/job-status/${this.jobId}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          if (retailResponse.ok) job = await retailResponse.json();
        } catch (e) { /* continue to APF fallback */ }

        if (!job) {
          const apfResponse = await fetch(`${API_BASE_URL}/api/job_status/${this.jobId}`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          if (apfResponse.ok) job = await apfResponse.json();
        }
      }

      if (!job) {
        throw new Error('Job not found');
      }
      
      // Reset error count on successful poll
      this.errorCount = 0;
      
      // Call progress callback
      this.onProgress(job);
      
      // Check terminal states
      if (job.status === 'completed') {
        this.stop();
        this.onComplete(job);
        console.log(`✅ Job ${this.jobId} completed`);
      } else if (job.status === 'failed') {
        this.stop();
        this.onError(job.error_message || 'Job failed');
        console.error(`❌ Job ${this.jobId} failed`);
      } else if (job.status === 'cancelled') {
        this.stop();
        this.onError('Job was cancelled');
        console.warn(`⚠️ Job ${this.jobId} cancelled`);
      }
      
    } catch (error) {
      console.error(`Polling error for job ${this.jobId}:`, error);
      
      this.errorCount++;
      
      // Stop after too many errors
      if (this.errorCount >= this.maxErrors) {
        this.stop();
        this.onError(`Failed to fetch job status after ${this.maxErrors} attempts`);
        console.error(`❌ Stopped polling job ${this.jobId} due to repeated errors`);
      }
      
      // Don't stop on network errors, keep trying
      // (might be temporary network issue)
    }
  }

  /**
   * Stop polling
   */
  stop() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.isRunning = false;
    console.log(`⏹️ Stopped polling job ${this.jobId}`);
  }

  /**
   * Update poll delay (useful for adaptive polling)
   */
  setPollDelay(delayMs) {
    this.pollDelay = delayMs;
    
    // Restart interval with new delay if currently running
    if (this.isRunning && this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = setInterval(() => this.poll(), this.pollDelay);
    }
  }
}


/**
 * Adaptive Job Tracker
 * 
 * Automatically adjusts polling frequency based on job progress.
 * - Early stages (0-20%): Poll every 2 seconds (fast progress)
 * - Middle stages (20-80%): Poll every 3 seconds (normal)
 * - Late stages (80-100%): Poll every 5 seconds (slower progress)
 */
class AdaptiveJobTracker extends JobTracker {
  constructor(jobId, callbacks = {}) {
    super(jobId, callbacks);
    this.lastProgress = 0;
  }

  async poll() {
    await super.poll();
    
    // Don't adjust if not running anymore
    if (!this.isRunning) return;
    
    // Adjust polling based on progress
    const progress = this.lastProgress;
    
    let newDelay;
    if (progress < 20) {
      newDelay = 2000; // Fast polling in early stages
    } else if (progress < 80) {
      newDelay = 3000; // Normal polling
    } else {
      newDelay = 5000; // Slower polling near end
    }
    
    if (newDelay !== this.pollDelay) {
      this.setPollDelay(newDelay);
      console.log(`⏱️ Adjusted poll delay to ${newDelay}ms (progress: ${progress}%)`);
    }
  }

  onProgress(job) {
    this.lastProgress = job.progress_percent || 0;
    super.onProgress(job);
  }
}


export default JobTracker;
export { AdaptiveJobTracker };
